import { useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Navigation, Footer, WhatsAppButton, CTASection, PageHeader } from "@/components/Layout";
import { Target, Lightbulb, Settings, Globe, CheckCircle2, Building2, Network, Cpu, Leaf } from "lucide-react";

export default function ServicesPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="services-page" className="min-h-screen">
      <Navigation isScrolled={true} forceDark={true} />
      
      <PageHeader 
        badge="Our Services"
        title="What We Do"
        subtitle="We support organisations across the full journey from strategic thinking to execution."
      />

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="space-y-24">
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="service-icon-container mb-6 w-16 h-16"><Target className="text-cyan-500" size={32} strokeWidth={1.5} /></div>
                <h2 className="text-3xl font-bold text-slate-900 mb-4">Strategy</h2>
                <p className="text-slate-600 text-lg mb-8">Helping organisations define direction, make trade-offs, and prioritise what matters most.</p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-slate-600"><CheckCircle2 className="text-cyan-500 flex-shrink-0" size={20} />Corporate and business strategy</li>
                  <li className="flex items-center gap-3 text-slate-600"><CheckCircle2 className="text-cyan-500 flex-shrink-0" size={20} />Growth and market entry</li>
                  <li className="flex items-center gap-3 text-slate-600"><CheckCircle2 className="text-cyan-500 flex-shrink-0" size={20} />Portfolio and investment prioritisation</li>
                  <li className="flex items-center gap-3 text-slate-600"><CheckCircle2 className="text-cyan-500 flex-shrink-0" size={20} />Operating model design</li>
                  <li className="flex items-center gap-3 text-slate-600"><CheckCircle2 className="text-cyan-500 flex-shrink-0" size={20} />Executive and board-level strategy support</li>
                </ul>
              </div>
              <div>
                <Card className="bg-gradient-to-br from-slate-50 to-cyan-50 border-slate-100 rounded-2xl p-12 text-center">
                  <Target className="text-cyan-500 mx-auto mb-6" size={80} strokeWidth={1} />
                  <p className="text-slate-500 italic">"Helping organisations define direction, make trade-offs, and prioritise what matters most."</p>
                </Card>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="lg:order-2">
                <div className="service-icon-container mb-6 w-16 h-16"><Lightbulb className="text-cyan-500" size={32} strokeWidth={1.5} /></div>
                <h2 className="text-3xl font-bold text-slate-900 mb-4">Innovation & New Ventures</h2>
                <p className="text-slate-600 text-lg mb-8">Designing and launching new ideas that are grounded in reality.</p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-slate-600"><CheckCircle2 className="text-cyan-500 flex-shrink-0" size={20} />Innovation strategy and capability building</li>
                  <li className="flex items-center gap-3 text-slate-600"><CheckCircle2 className="text-cyan-500 flex-shrink-0" size={20} />Venture and business model design</li>
                  <li className="flex items-center gap-3 text-slate-600"><CheckCircle2 className="text-cyan-500 flex-shrink-0" size={20} />Technology-enabled solutions</li>
                  <li className="flex items-center gap-3 text-slate-600"><CheckCircle2 className="text-cyan-500 flex-shrink-0" size={20} />Pilot design and scale-up pathways</li>
                  <li className="flex items-center gap-3 text-slate-600"><CheckCircle2 className="text-cyan-500 flex-shrink-0" size={20} />Ecosystem and partnership innovation</li>
                </ul>
              </div>
              <div className="lg:order-1">
                <Card className="bg-gradient-to-br from-slate-50 to-cyan-50 border-slate-100 rounded-2xl p-12 text-center">
                  <Lightbulb className="text-cyan-500 mx-auto mb-6" size={80} strokeWidth={1} />
                  <p className="text-slate-500 italic">"Designing and launching new ideas that are grounded in reality."</p>
                </Card>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="service-icon-container mb-6 w-16 h-16"><Settings className="text-cyan-500" size={32} strokeWidth={1.5} /></div>
                <h2 className="text-3xl font-bold text-slate-900 mb-4">Transformation & Delivery</h2>
                <p className="text-slate-600 text-lg mb-8">Supporting the execution of complex initiatives from concept through delivery.</p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-slate-600"><CheckCircle2 className="text-cyan-500 flex-shrink-0" size={20} />Transformation roadmaps</li>
                  <li className="flex items-center gap-3 text-slate-600"><CheckCircle2 className="text-cyan-500 flex-shrink-0" size={20} />Programme and portfolio leadership</li>
                  <li className="flex items-center gap-3 text-slate-600"><CheckCircle2 className="text-cyan-500 flex-shrink-0" size={20} />Change and stakeholder management</li>
                  <li className="flex items-center gap-3 text-slate-600"><CheckCircle2 className="text-cyan-500 flex-shrink-0" size={20} />Governance and decision frameworks</li>
                  <li className="flex items-center gap-3 text-slate-600"><CheckCircle2 className="text-cyan-500 flex-shrink-0" size={20} />Performance tracking and outcomes</li>
                </ul>
              </div>
              <div>
                <Card className="bg-gradient-to-br from-slate-50 to-cyan-50 border-slate-100 rounded-2xl p-12 text-center">
                  <Settings className="text-cyan-500 mx-auto mb-6" size={80} strokeWidth={1} />
                  <p className="text-slate-500 italic">"Supporting the execution of complex initiatives from concept through delivery."</p>
                </Card>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="lg:order-2">
                <div className="service-icon-container mb-6 w-16 h-16"><Globe className="text-cyan-500" size={32} strokeWidth={1.5} /></div>
                <h2 className="text-3xl font-bold text-slate-900 mb-4">Systems, Infrastructure & Sustainability</h2>
                <p className="text-slate-600 text-lg mb-8">Working across sectors to address interconnected challenges.</p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-slate-600"><CheckCircle2 className="text-cyan-500 flex-shrink-0" size={20} />Infrastructure and precinct strategy</li>
                  <li className="flex items-center gap-3 text-slate-600"><CheckCircle2 className="text-cyan-500 flex-shrink-0" size={20} />Urban regeneration and place-based development</li>
                  <li className="flex items-center gap-3 text-slate-600"><CheckCircle2 className="text-cyan-500 flex-shrink-0" size={20} />Sustainability and ESG integration</li>
                  <li className="flex items-center gap-3 text-slate-600"><CheckCircle2 className="text-cyan-500 flex-shrink-0" size={20} />Energy, water, and resource systems</li>
                  <li className="flex items-center gap-3 text-slate-600"><CheckCircle2 className="text-cyan-500 flex-shrink-0" size={20} />Multi-stakeholder collaboration models</li>
                </ul>
              </div>
              <div className="lg:order-1">
                <Card className="bg-gradient-to-br from-slate-50 to-cyan-50 border-slate-100 rounded-2xl p-12 text-center">
                  <Globe className="text-cyan-500 mx-auto mb-6" size={80} strokeWidth={1} />
                  <p className="text-slate-500 italic">"Working across sectors to address interconnected challenges."</p>
                </Card>
              </div>
            </div>

          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-2 rounded-full text-sm font-medium bg-cyan-50 text-cyan-600 mb-6">Sectors</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">Where We <span className="text-gradient">Work</span></h2>
            <p className="text-slate-600 text-lg">We work in environments where complexity, scale, and long-term impact intersect.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="card-hover bg-white border-slate-100 rounded-2xl">
              <CardContent className="p-6">
                <div className="service-icon-container mb-4"><Building2 className="text-cyan-500" size={24} strokeWidth={1.5} /></div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Natural Resources</h3>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-slate-500 text-sm"><div className="w-1 h-1 rounded-full bg-cyan-500" />Mining and minerals</li>
                  <li className="flex items-center gap-2 text-slate-500 text-sm"><div className="w-1 h-1 rounded-full bg-cyan-500" />Energy systems and transition</li>
                  <li className="flex items-center gap-2 text-slate-500 text-sm"><div className="w-1 h-1 rounded-full bg-cyan-500" />Water security and treatment</li>
                </ul>
              </CardContent>
            </Card>
            <Card className="card-hover bg-white border-slate-100 rounded-2xl">
              <CardContent className="p-6">
                <div className="service-icon-container mb-4"><Network className="text-cyan-500" size={24} strokeWidth={1.5} /></div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Infrastructure</h3>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-slate-500 text-sm"><div className="w-1 h-1 rounded-full bg-cyan-500" />Transport and logistics</li>
                  <li className="flex items-center gap-2 text-slate-500 text-sm"><div className="w-1 h-1 rounded-full bg-cyan-500" />Urban development</li>
                  <li className="flex items-center gap-2 text-slate-500 text-sm"><div className="w-1 h-1 rounded-full bg-cyan-500" />Public infrastructure</li>
                </ul>
              </CardContent>
            </Card>
            <Card className="card-hover bg-white border-slate-100 rounded-2xl">
              <CardContent className="p-6">
                <div className="service-icon-container mb-4"><Cpu className="text-cyan-500" size={24} strokeWidth={1.5} /></div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Technology</h3>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-slate-500 text-sm"><div className="w-1 h-1 rounded-full bg-cyan-500" />AI and analytics</li>
                  <li className="flex items-center gap-2 text-slate-500 text-sm"><div className="w-1 h-1 rounded-full bg-cyan-500" />Digital platforms</li>
                  <li className="flex items-center gap-2 text-slate-500 text-sm"><div className="w-1 h-1 rounded-full bg-cyan-500" />Emerging technologies</li>
                </ul>
              </CardContent>
            </Card>
            <Card className="card-hover bg-white border-slate-100 rounded-2xl">
              <CardContent className="p-6">
                <div className="service-icon-container mb-4"><Leaf className="text-cyan-500" size={24} strokeWidth={1.5} /></div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Sustainability</h3>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-slate-500 text-sm"><div className="w-1 h-1 rounded-full bg-cyan-500" />ESG integration</li>
                  <li className="flex items-center gap-2 text-slate-500 text-sm"><div className="w-1 h-1 rounded-full bg-cyan-500" />Climate strategy</li>
                  <li className="flex items-center gap-2 text-slate-500 text-sm"><div className="w-1 h-1 rounded-full bg-cyan-500" />Resource efficiency</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <CTASection />
      <Footer />
      <WhatsAppButton />
    </div>
  );
}
